import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef, useImperativeHandle, useRef, useContext, useState, useEffect } from 'react';
import { cls, componentWrapper } from '@arco-design/mobile-utils';
import { BaseImage } from '../image';
import IconUserFill from '../icon/IconUserFill';
import { GlobalContext } from '../context-provider';
import Group, { AvatarGroupContext } from './group';
export * from './type';
export function componentGenerator(Comp) {
  return /*#__PURE__*/forwardRef(function (props, ref) {
    var _cls;

    var _useContext = useContext(GlobalContext),
        prefixCls = _useContext.prefixCls;

    var _useContext2 = useContext(AvatarGroupContext),
        groupShape = _useContext2.shape,
        groupSize = _useContext2.size,
        isGroup = _useContext2.isGroup;

    var _props$shape = props.shape,
        shape = _props$shape === void 0 ? props.shape || (isGroup ? groupShape : 'circle') : _props$shape,
        _props$size = props.size,
        size = _props$size === void 0 ? props.size || (isGroup ? groupSize : 'small') : _props$size,
        src = props.src,
        _props$decoration = props.decoration,
        decoration = _props$decoration === void 0 ? null : _props$decoration,
        _props$textAvatar = props.textAvatar,
        textAvatar = _props$textAvatar === void 0 ? '' : _props$textAvatar,
        _props$avatarStyle = props.avatarStyle,
        avatarStyle = _props$avatarStyle === void 0 ? {} : _props$avatarStyle,
        _props$autoFixFontSiz = props.autoFixFontSize,
        autoFixFontSize = _props$autoFixFontSiz === void 0 ? true : _props$autoFixFontSiz,
        _props$autoFixFontOff = props.autoFixFontOffset,
        autoFixFontOffset = _props$autoFixFontOff === void 0 ? 2 : _props$autoFixFontOff,
        _props$avatarName = props.avatarName,
        avatarName = _props$avatarName === void 0 ? '' : _props$avatarName,
        _props$avatarDesc = props.avatarDesc,
        avatarDesc = _props$avatarDesc === void 0 ? '' : _props$avatarDesc,
        _props$children = props.children,
        children = _props$children === void 0 ? null : _props$children,
        _props$renderInfo = props.renderInfo,
        renderInfo = _props$renderInfo === void 0 ? null : _props$renderInfo,
        _props$className = props.className,
        className = _props$className === void 0 ? '' : _props$className,
        _props$style = props.style,
        style = _props$style === void 0 ? {} : _props$style,
        _props$imageProps = props.imageProps,
        imageProps = _props$imageProps === void 0 ? {} : _props$imageProps,
        _props$defaultOverLap = props.defaultOverLap,
        defaultOverLap = _props$defaultOverLap === void 0 ? /*#__PURE__*/React.createElement(IconUserFill, {
      className: prefixCls + "-avatar-default"
    }) : _props$defaultOverLap,
        onClickDecoration = props.onClickDecoration,
        onClick = props.onClick;
    var domRef = useRef(null);
    var avatarRef = useRef(null);
    var avatarTextRef = useRef(null);

    var _useState = useState(1),
        textScale = _useState[0],
        setTextScale = _useState[1];

    useImperativeHandle(ref, function () {
      return {
        dom: domRef.current
      };
    });
    var isImageAvatar = !children && src !== void 0; // 图片头像

    var isTextAvatar = !children && !isImageAvatar && textAvatar; // 文字头像

    var useDefaultAvatar = !children && !isImageAvatar && !isTextAvatar; // 使用默认头像

    useEffect(function () {
      getComputedTextScale();
    }, [textAvatar]);
    /** 根据字体元素宽度和头像大小动态调整缩放比例 */

    function getComputedTextScale() {
      var _avatarRef$current, _avatarTextRef$curren;

      if (!isTextAvatar || !autoFixFontSize || !avatarRef || !avatarTextRef) return;

      var _ref = ((_avatarRef$current = avatarRef.current) == null ? void 0 : _avatarRef$current.getBoundingClientRect()) || {},
          _ref$width = _ref.width,
          avatarWidth = _ref$width === void 0 ? 0 : _ref$width;

      var _ref2 = ((_avatarTextRef$curren = avatarTextRef.current) == null ? void 0 : _avatarTextRef$curren.getBoundingClientRect()) || {},
          _ref2$width = _ref2.width,
          textWidth = _ref2$width === void 0 ? 0 : _ref2$width;

      if (avatarWidth === 0 || textWidth === 0) return; // 文本节点宽度大于头像元素时进行动态缩放

      var maxWidth = avatarWidth - 2 * autoFixFontOffset;

      if (textWidth > maxWidth) {
        var scale = maxWidth / textWidth;
        setTextScale(textScale * scale);
      }
    }

    return /*#__PURE__*/React.createElement("div", {
      ref: domRef,
      style: style,
      className: cls(prefixCls + "-avatar-wrapper", className, size, shape, {
        'with-info': Boolean(avatarName)
      }),
      onClick: onClick
    }, /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-avatar", size, shape, (_cls = {}, _cls[prefixCls + "-text-avatar"] = isTextAvatar, _cls[prefixCls + "-image-avatar"] = isImageAvatar, _cls['default-overlap'] = useDefaultAvatar, _cls)),
      style: avatarStyle,
      ref: avatarRef
    }, children, useDefaultAvatar && defaultOverLap, isImageAvatar && src && /*#__PURE__*/React.createElement(Comp, _extends({
      className: prefixCls + "-avatar-img",
      src: src
    }, imageProps)), isTextAvatar && /*#__PURE__*/React.createElement("span", {
      className: prefixCls + "-avatar-text",
      ref: avatarTextRef,
      style: {
        transform: "scale(" + textScale + ")"
      }
    }, textAvatar), decoration && /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-avatar-decoration",
      onClick: onClickDecoration
    }, decoration)), renderInfo, !renderInfo && avatarName && /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-avatar-info")
    }, /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-avatar-name")
    }, avatarName), avatarDesc && /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-avatar-desc")
    }, avatarDesc)));
  });
}
var Avatar = componentGenerator(BaseImage);
/**
 * 头像展示组件，支持圆形和方形两种形状，支持图片文字头像，支持五种尺寸。
 * @type 信息展示
 * @name 头像
 */

export default componentWrapper(Avatar, {
  Group: Group
});